'use client';

import React, { useCallback } from 'react';
import { useSPARouter } from '../../context/SPARouterContext';

// Prefetch cache to avoid duplicate prefetches
const prefetched = new Set();
function prefetchRoute(href) {
  if (!href || prefetched.has(href) || typeof href !== 'string') return;
  if (href.startsWith('http') || href.startsWith('mailto:')) return;
  prefetched.add(href);
  // Prefetch the JS chunk by creating a hidden link prefetch
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  document.head.appendChild(link);
}

function isAuthPath(path) {
  return /^\/login(?:[/?#]|$)/.test(path);
}

function withLoginRedirect(url) {
  if (typeof window === 'undefined') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (!/^\/(?:[a-z]{2}-[a-z]{2}\/)?login(?:[/?#]|$)/i.test(parsed.pathname)) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
    if (!parsed.searchParams.has('redirect')) {
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (!/^\/(?:[a-z]{2}-[a-z]{2}\/)?login(?:[/?#]|$)/i.test(window.location.pathname)) {
        parsed.searchParams.set('redirect', current);
      }
    }
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return url;
  }
}

function isOutsideMainSPA() {
  return typeof document !== 'undefined' && !document.getElementById('main-content');
}

const LangLink = React.forwardRef(({ to, href, children, onClick, ...props }, ref) => {
  const { push, lang } = useSPARouter();
  const target = to || href || '';
  const prefixed = typeof target === 'string' && target.startsWith('/')
    ? `/${lang}${target}`
    : target;

  const handleClick = useCallback((e) => {
    if (onClick) onClick(e);
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (typeof prefixed === 'string' && (prefixed.startsWith('http') || prefixed.startsWith('mailto:'))) return;

    if (typeof target === 'string' && isAuthPath(target)) {
      e.preventDefault();
      window.location.assign(withLoginRedirect(prefixed));
      return;
    }

    // In auth route tree (e.g. /login), pushState alone won't switch to main layout.
    // Use hard navigation to ensure route tree changes correctly.
    if (isOutsideMainSPA()) {
      e.preventDefault();
      window.location.assign(prefixed);
      return;
    }

    e.preventDefault();
    push(prefixed);
  }, [onClick, prefixed, push, target]);

  // Prefetch on pointer enter (hover / touch)
  const handlePointerEnter = useCallback(() => {
    prefetchRoute(prefixed);
  }, [prefixed]);

  return (
    <a
      ref={ref}
      href={prefixed}
      onClick={handleClick}
      onPointerEnter={handlePointerEnter}
      {...props}
    >
      {children}
    </a>
  );
});

LangLink.displayName = 'LangLink';

export default LangLink;
